import { useState, type ReactNode } from 'react';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { StarRating } from '@/components/ui/StarRating';
import { BookCover } from '@/components/books/BookCover';
import { loggedBooksService } from '@/services/logged-books.service';
import { ApiError } from '@/services/http';
import { LogBookDialog } from './LogBookDialog';
import type { BookmarkView, LoggedBookView } from '@/types/api';

/**
 * ReadingLogSection — the visually distinct "Reading Log" band beneath the
 * In Progress / Saved shelves. It gathers every finished book in one place:
 *   - catalog books marked completed, rendered by the page via
 *     `renderBookmarkTile` so they keep their status control + detail modal;
 *   - off-catalog entries the reader logged by ISBN or manually.
 * Owns the logged-book add/edit dialog + delete confirmation; logged-book
 * mutations are lifted to the page via `onChange` so the Reviews & ratings
 * section (which merges those in) stays in sync.
 */
export interface ReadingLogSectionProps {
  readerId: string;
  readerName: string;
  books: LoggedBookView[];
  onChange: (next: LoggedBookView[]) => void;
  /** Catalog bookmarks with status 'completed', folded into this section. */
  completedBookmarks: BookmarkView[];
  /** Renders a completed catalog bookmark as a shelf tile. Owned by the page
   *  so the status-control / remove / modal wiring lives with the shelves. */
  renderBookmarkTile: (bookmark: BookmarkView) => ReactNode;
}

function formatFinished(dateOnly: string | null): string | null {
  if (!dateOnly) return null;
  const d = new Date(`${dateOnly}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function ReadingLogSection({
  readerId,
  readerName,
  books,
  onChange,
  completedBookmarks,
  renderBookmarkTile,
}: ReadingLogSectionProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<LoggedBookView | null>(null);
  const [pendingDelete, setPendingDelete] = useState<LoggedBookView | null>(null);
  const [deleting, setDeleting] = useState(false);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (book: LoggedBookView) => {
    setEditing(book);
    setDialogOpen(true);
  };

  const handleSaved = (saved: LoggedBookView) => {
    const exists = books.some((b) => b.id === saved.id);
    onChange(exists ? books.map((b) => (b.id === saved.id ? saved : b)) : [saved, ...books]);
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await loggedBooksService.remove(pendingDelete.id);
      onChange(books.filter((b) => b.id !== pendingDelete.id));
      toast.success(`Removed “${pendingDelete.title}”.`);
      setPendingDelete(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not remove that entry.');
    } finally {
      setDeleting(false);
    }
  };

  const isEmpty = completedBookmarks.length === 0 && books.length === 0;

  return (
    <section className="border-y border-or-slate-200 py-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="or-kicker mb-1.5">Reading Log</div>
          <h2 className="font-display text-[18px] font-bold text-or-slate-900">
            Books {readerName} has finished
          </h2>
        </div>
        <Button variant="primary" size="sm" onClick={openCreate}>
          <Plus className="mr-1.5 h-4 w-4" aria-hidden />
          Manually Log a Book
        </Button>
      </div>

      <div className="mt-4">
        {isEmpty ? (
          <p className="rounded-2xl border border-dashed border-or-slate-200 bg-white/80 px-5 py-6 text-center text-[13px] text-or-slate-500">
            Finished books land here. Mark a catalog book{' '}
            <span className="font-semibold text-or-slate-700">Completed</span>, or tap{' '}
            <span className="font-semibold text-or-slate-700">Manually Log a Book</span> to add one
            that isn&rsquo;t in our library.
          </p>
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {/* Completed catalog books first, then off-catalog logged books. */}
            {completedBookmarks.map((bm) => renderBookmarkTile(bm))}
            {books.map((book) => (
              <LoggedBookCard
                key={book.id}
                book={book}
                onEdit={() => openEdit(book)}
                onDelete={() => setPendingDelete(book)}
              />
            ))}
          </ul>
        )}
      </div>

      <LogBookDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        readerId={readerId}
        editing={editing}
        onSaved={handleSaved}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(open) => {
          if (!open) setPendingDelete(null);
        }}
        title="Remove from Reading Log?"
        description={
          pendingDelete
            ? `“${pendingDelete.title}” will be removed from ${readerName}'s Reading Log.`
            : undefined
        }
        confirmLabel="Remove"
        onConfirm={handleConfirmDelete}
        isConfirming={deleting}
      />
    </section>
  );
}

/**
 * LoggedBookCard — a single off-catalog Reading Log entry. Same tile shell as
 * the catalog shelf tiles, but with edit/delete affordances (no status control
 * or detail modal) and a "Not in our library" tag to distinguish it.
 */
function LoggedBookCard({
  book,
  onEdit,
  onDelete,
}: {
  book: LoggedBookView;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const finished = formatFinished(book.finishedOn);
  return (
    <li className="group flex gap-3 rounded-2xl border border-or-slate-100 bg-white p-4 shadow-sm">
      <BookCover
        title={book.title}
        author={book.author}
        src={book.coverUrl}
        seed={book.id}
        w={56}
        h={80}
        className="h-20 w-14 shrink-0 rounded-lg"
      />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="truncate font-display text-[14px] font-bold text-or-slate-900">
              {book.title}
            </div>
            {book.author ? (
              <div className="truncate text-[12px] text-or-slate-500">{book.author}</div>
            ) : null}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={onEdit}
              aria-label={`Edit ${book.title}`}
              className="inline-flex h-7 w-7 items-center justify-center rounded-full text-or-slate-400 transition hover:bg-or-slate-100 hover:text-or-slate-600"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden />
            </button>
            <button
              type="button"
              onClick={onDelete}
              aria-label={`Remove ${book.title}`}
              className="inline-flex h-7 w-7 items-center justify-center rounded-full text-or-rose-400 transition hover:bg-or-rose-100 hover:text-or-rose-600"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </button>
          </div>
        </div>

        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          {book.rating ? <StarRating value={book.rating} readOnly size="sm" /> : null}
          {finished ? (
            <span className="text-[12px] text-or-slate-500">Finished {finished}</span>
          ) : null}
          {book.pageCount ? (
            <span className="text-[12px] text-or-slate-500">{book.pageCount} pages</span>
          ) : null}
        </div>

        <span className="mt-1 w-fit rounded-full bg-or-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-or-slate-500">
          Not in our library
        </span>
      </div>
    </li>
  );
}
